import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Modal, Row, Space, Spin } from 'antd';
import { DeleteOutlined, EditOutlined, FileDoneOutlined, FileExclamationOutlined, PlusCircleOutlined, UserAddOutlined, UserDeleteOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { axiosErrorHandler, isNullOrUndefined, showSuccessNotification } from '../../../../../swzutils';
import CustomerForm, {} from "../CustomerForm";
import CustomerUserInviteModal from "../CustomerUserInviteModal";
import HasPermissions from "../../../../../containers/HasPermissions";
import { CustomerContactType } from "../api/Representation";
import CustomerResource from "../api/CustomerResource";
import PermissionDropdownButton from "../../../components/PermissionDropdownButton";
import CustomerDebtorModal from "../CustomerDebtorModal";
import DebtorDefinitionList from "../../debtors/DebtorDefinitionList";
import { userHasPermission } from "../../../../../swzutils/UserPermissionsUtil";
import { loggedInUserSelector } from "../../../../../store/slice/auth";
import { useAppSelector } from "../../../../../store";
import PaginatedFilterableTable, {} from "../../../components/table/PaginatedFilterableTable";
import CustomerContactResource from "../api/CustomerContactResource";
import CustomerContactUpdateModal from "../CustomerContactUpdateModal";
import dayjs from "dayjs";
import ActiveStatusTag from "../../../components/ActiveStatusTag";
import DateTimeModal from "../../../components/DateTimeModal";
const CustomerEditPage = ({ customerId }) => {
    const [networkRequestInProgress, setNetworkRequestInProgress] = useState(false);
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [linkDebtorModalOpen, setLinkDebtorModalOpen] = useState(false);
    const [customerContactModalOpen, setCustomerContactModalOpen] = useState(false);
    const [dateTimeOpen, setDateTimeOpen] = useState(false);
    const [customer, setCustomer] = useState(undefined);
    const [selectedCustomerContact, setSelectedCustomerContact] = useState(undefined);
    const [customerContactType, setCustomerContactType] = useState(CustomerContactType.OFFICE);
    const contactsTableRef = useRef(null);
    const customerForm = useRef(null);
    const navigate = useNavigate();
    const [modal, contextHolder] = Modal.useModal();
    const loggedInUser = useAppSelector(loggedInUserSelector);
    useEffect(() => {
        if (customerId) {
            loadCustomer(customerId);
        }
    }, [customerId]);
    const backToList = () => {
        navigate("/app/customers/list");
    };
    const loadCustomer = (customerId) => {
        if (!customerId) {
            return;
        }
        setNetworkRequestInProgress(true);
        CustomerResource.get(customerId, setCustomer, axiosErrorHandler, () => setNetworkRequestInProgress(false));
    };
    const ostraciseCustomerUser = (customerContact) => {
        setNetworkRequestInProgress(true);
        CustomerContactResource.ostraciseCustomerUser(customerId, customerContact.id, () => {
            showSuccessNotification(`Customer portal access for ${customerContact.firstName} ${customerContact.lastName} has been removed`);
            refreshContacts();
        }, axiosErrorHandler, () => setNetworkRequestInProgress(false));
    };
    const patchCustomerUser = (customerContact, showPickupDockets) => {
        setNetworkRequestInProgress(true);
        const request = {
            showPickupDockets
        };
        CustomerContactResource.patchCustomerUserAccess(customerId, customerContact.id, request, refreshContacts, axiosErrorHandler, () => setNetworkRequestInProgress(false));
    };
    const confirmDeleteCustomerUserAccess = (customerContact) => {
        modal.confirm({
            title: 'Are you sure?',
            content: `Are you sure you want to remove portal access from ${customerContact.firstName} ${customerContact.lastName}?`,
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => ostraciseCustomerUser(customerContact)
        });
    };
    const enableDisableCustomerContact = (contact, disableAt) => {
        setNetworkRequestInProgress(true);
        CustomerContactResource.patch(customerId, contact.id, disableAt, refreshContacts, axiosErrorHandler, () => setNetworkRequestInProgress(false));
        setDateTimeOpen(false);
        setSelectedCustomerContact(undefined);
    };
    const emailCustomerUserInvite = (customerContact) => {
        setSelectedCustomerContact(customerContact);
        setInviteModalOpen(true);
    };
    const confirmUnlinkDebtor = () => {
        modal.confirm({
            title: 'Are you sure?',
            content: `Are you sure you want to remove the Debtor from the Customer?`,
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => {
                setNetworkRequestInProgress(true);
                CustomerResource.unlinkDebtor(customerId, customer => setCustomer(customer), axiosErrorHandler, () => setNetworkRequestInProgress(false));
            }
        });
    };
    const confirmShowPickupDockets = (customerContact) => {
        modal.confirm({
            title: 'Are you sure?',
            content: `Are you sure you want to allow ${customerContact.firstName} ${customerContact.lastName} to see pickup dockets?`,
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => patchCustomerUser(customerContact, true)
        });
    };
    const hideInviteCustomerModal = () => {
        setInviteModalOpen(false);
    };
    const showCustomerContactModal = (type, contact) => {
        setSelectedCustomerContact(contact);
        setCustomerContactType(contact?.type ?? type);
        setCustomerContactModalOpen(true);
    };
    const closeCustomerContactModal = () => {
        setCustomerContactModalOpen(false);
        setSelectedCustomerContact(undefined);
    };
    const refreshContacts = () => {
        contactsTableRef.current?.fetch();
    };
    const columns = [{
            title: "First Name",
            dataIndex: "firstName"
        }, {
            title: "Last Name",
            dataIndex: "lastName"
        }, {
            title: "Email",
            dataIndex: "emailAddress",
        }, {
            title: "Phone",
            dataIndex: "telephone",
        }, {
            title: "Type",
            dataIndex: "type"
        }, {
            title: "Position",
            dataIndex: "position",
        }, {
            key: 'active',
            title: 'Active',
            dataIndex: 'disabledAt',
            render: (text, record) => {
                const isActive = !record.disabledAt || dayjs().isBefore(dayjs(record.disabledAt));
                return (_jsx(ActiveStatusTag, { active: isActive }));
            }
        }, {
            title: "Portal Access?",
            dataIndex: "userAccess",
            render: (text, record) => {
                return record.userAccess ? "Yes" : "No";
            }
        }, {
            title: "Show Quarry Dockets?",
            dataIndex: "showPickupDockets",
            render: (text, record) => {
                return record.userAccess?.showPickupDockets ? "Yes" : "No";
            }
        }, {
            title: 'Action',
            key: 'action',
            render: (text, record) => (_jsx(PermissionDropdownButton, { label: _jsxs(_Fragment, { children: [_jsx(EditOutlined, {}), " Edit"] }), onClick: () => showCustomerContactModal(record.type, record), permissions: ['admin:customerContact:update'], menuItems: [{
                        key: "enableCustomerContact",
                        permissions: ['admin:customerContact:update'],
                        condition: !!record.disabledAt,
                        onClick: () => enableDisableCustomerContact(record, null),
                        icon: _jsx(PlusCircleOutlined, {}),
                        label: "Enable"
                    }, {
                        key: "disableCustomerContact",
                        permissions: ['admin:customerContact:update'],
                        condition: isNullOrUndefined(record.disabledAt),
                        onClick: () => {
                            setSelectedCustomerContact(record);
                            setDateTimeOpen(true);
                        },
                        icon: _jsx(DeleteOutlined, {}),
                        label: "Disable"
                    }, {
                        key: "ostraciseCustomerContact",
                        permissions: ['admin:customerContact:ostracise'],
                        condition: !!record.userAccess,
                        onClick: () => confirmDeleteCustomerUserAccess(record),
                        icon: _jsx(UserDeleteOutlined, {}),
                        label: "Remove Portal Access"
                    }, {
                        key: "sendCustomerUserInvite",
                        permissions: ['admin:customerContact:invite'],
                        condition: isNullOrUndefined(record.disabledAt),
                        onClick: () => emailCustomerUserInvite(record),
                        icon: _jsx(UserAddOutlined, {}),
                        label: record.userAccess ? "Resend Invite" : "Invite to Portal"
                    }, {
                        key: "showPickupDockets",
                        permissions: ['admin:customerContact:invite'],
                        condition: record.userAccess && !record.userAccess?.showPickupDockets,
                        onClick: () => confirmShowPickupDockets(record),
                        icon: _jsx(FileDoneOutlined, {}),
                        label: "Allow Pickup Dockets"
                    }, {
                        key: "hidePickupDockets",
                        permissions: ['admin:customerContact:invite'],
                        condition: record.userAccess && record.userAccess.showPickupDockets,
                        onClick: () => patchCustomerUser(record, false),
                        icon: _jsx(FileExclamationOutlined, {}),
                        label: "Disallow Pickup Dockets"
                    }] }))
        }];
    const showDebtor = !!(customerId && userHasPermission(loggedInUser, ['admin:customerDebtor:view']));
    const hasDebtor = !!(customerId && !!customer?.debtor);
    const canEditDebtor = userHasPermission(loggedInUser, ['admin:customerDebtor:update']);
    const canRemoveDebtor = hasDebtor && userHasPermission(loggedInUser, ['admin:customerDebtor:delete']);
    return (_jsxs(Spin, { spinning: networkRequestInProgress, children: [contextHolder, _jsxs(Row, { gutter: 8, className: "no-margin", children: [" ", _jsx(Col, { sm: 24, md: 12, children: _jsx(Card, { title: "Customer", type: "inner", variant: 'borderless', children: _jsx(CustomerForm, { tenantId: customer?.tenant.id, ref: customerForm, customer: customer, onSave: backToList }) }) }), showDebtor &&
                        _jsx(Col, { sm: 24, md: 12, children: _jsx(Card, { title: "Debtor", type: "inner", variant: 'borderless', extra: _jsxs(_Fragment, { children: [canEditDebtor &&
                                            _jsx(Button, { htmlType: "button", className: "pull-right", onClick: () => setLinkDebtorModalOpen(true), icon: _jsx(EditOutlined, {}), children: "Edit" }), canRemoveDebtor &&
                                            _jsx(Button, { htmlType: "button", className: "pull-right", onClick: confirmUnlinkDebtor, icon: _jsx(DeleteOutlined, {}), children: "Remove" })] }), children: customer?.debtor
                                    ? _jsx(DebtorDefinitionList, { debtor: customer?.debtor })
                                    : _jsx("p", { children: "No Debtor" }) }) }), _jsx(HasPermissions, { permissions: ['admin:customerContact:list'], children: _jsx(Col, { sm: 24, md: showDebtor ? 24 : 12, children: _jsx(Card, { title: "Contacts", type: "inner", variant: 'borderless', extra: customerId && userHasPermission(loggedInUser, ['admin:customerContact:create']) &&
                                    _jsxs(Space, { className: "pull-right", children: [_jsx(Button, { htmlType: "button", onClick: () => showCustomerContactModal(CustomerContactType.OFFICE), children: "Add Contact" }), _jsx(Button, { htmlType: "button", onClick: () => showCustomerContactModal(CustomerContactType.DRIVER), children: "Add Driver" })] }), children: customerId &&
                                    _jsx(PaginatedFilterableTable, { columns: columns, tableKey: "customerContacts", dataUri: `/admin/v1/customers/${customerId}/contacts`, ref: contactsTableRef, minWidthBeforeScrolling: 400 }) }) }) }), _jsx("p", { children: "\u00A0" })] }), _jsxs(Row, { gutter: 10, className: "no-margin", children: [" ", _jsx(Col, { xs: 12, md: 6, children: _jsx(Button, { block: true, type: "primary", onClick: customerForm.current?.submit, size: "large", children: "Save" }) }), _jsx(Col, { xs: 12, md: 6, children: _jsx(Button, { htmlType: "button", href: "#/app/customers/list", block: true, type: "default", size: "large", children: "Cancel" }) })] }), _jsx(HasPermissions, { permissions: ['admin:customerContact:update'], children: _jsx(DateTimeModal, { open: !!selectedCustomerContact && dateTimeOpen, title: "Disable customer contact", description: "Enter date and time from which this contact will be disabled", onCancel: () => setDateTimeOpen(false), onComplete: disableAt => enableDisableCustomerContact(selectedCustomerContact, disableAt) }) }), _jsx(HasPermissions, { permissions: ['admin:customerContact:invite'], children: _jsx(CustomerUserInviteModal, { open: inviteModalOpen, onCancel: hideInviteCustomerModal, customerId: customerId, customerContact: selectedCustomerContact, onSuccess: (customerContact) => {
                        hideInviteCustomerModal();
                        refreshContacts();
                        showSuccessNotification(`Invite sent to ${customerContact.emailAddress}`);
                    } }) }), _jsx(CustomerDebtorModal, { open: linkDebtorModalOpen, customer: customer, onCancel: () => {
                    setLinkDebtorModalOpen(false);
                }, onSuccess: (customer) => {
                    setCustomer(customer);
                    setLinkDebtorModalOpen(false);
                } }), _jsx(HasPermissions, { permissions: ['admin:customerContact:create', 'admin:customerContact:update'], children: _jsx(CustomerContactUpdateModal, { defaultCustomerContactType: customerContactType, canChangeContactType: true, customerId: customerId, open: customerContactModalOpen, onCancel: closeCustomerContactModal, customerContact: selectedCustomerContact, onSave: () => {
                        closeCustomerContactModal();
                        refreshContacts();
                    } }) })] }));
};
export default CustomerEditPage;
